import "./index.css";

import React from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { AccessibilityProvider } from "@/components/ui/AccessibilityProvider";
import App from "./App";
import { analytics } from "@/lib/analytics";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";

const shouldLogSupabaseHost =
  import.meta.env.PROD || new URLSearchParams(window.location.search).get("debugSupabase") === "1";

if (shouldLogSupabaseHost) {
  try {
    const supabaseHost = new URL(SUPABASE_URL).host;
    console.log(`[Env] Supabase URL host: ${supabaseHost}`);
  } catch {
    console.warn("[Env] Supabase URL host: (invalid URL)");
  }
}

// CRITICAL FIX: Force Supabase to load session from cookies on app startup
// This ensures all RPC calls have Authorization: Bearer token
supabase.auth.getSession().then(({ data: { session } }) => {
  if (session) {
    console.debug('[Main] Session loaded on startup:', { userId: session.user?.id, hasToken: !!session.access_token });
  } else {
    console.debug('[Main] No session found on startup');
  }
});

// Validate environment alignment (CORS security)
const PUBLIC_APP_URL = import.meta.env.VITE_PUBLIC_APP_URL;
const currentOrigin = window.location.origin;

if (import.meta.env.DEV) {
  console.info('[Environment Check]', {
    VITE_PUBLIC_APP_URL: PUBLIC_APP_URL,
    currentOrigin,
    match: PUBLIC_APP_URL === currentOrigin
  });
}

if (PUBLIC_APP_URL && PUBLIC_APP_URL !== currentOrigin) {
  console.warn(
    `⚠️ Origin mismatch detected!\n` +
    `Expected: ${PUBLIC_APP_URL}\n` +
    `Actual: ${currentOrigin}\n` +
    `Edge functions may reject CORS requests. Update VITE_PUBLIC_APP_URL in .env`
  );
}

if (!PUBLIC_APP_URL) {
  console.error(
    `❌ VITE_PUBLIC_APP_URL is not set!\n` +
    `This will cause CORS errors in edge functions. Add to .env:\n` +
    `VITE_PUBLIC_APP_URL=${currentOrigin}`
  );
}

// Analytics is initialized automatically based on consent
// See src/lib/analytics.ts for consent-based initialization

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <AccessibilityProvider>
        <App />
      </AccessibilityProvider>
    </ThemeProvider>
  </React.StrictMode>
);
